import React, { useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { Phone, MapPin, Send, Loader2, ExternalLink, Mail } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';

const API = `${window.location.origin}/api`;

export default function Contact() {
  const { t } = useTranslation();
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [subscribeNewsletter, setSubscribeNewsletter] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/contact`, contactForm);
      
      // Si inscription newsletter cochée
      if (subscribeNewsletter && contactForm.email) {
        try {
          await axios.post(`${API}/newsletter`, { email: contactForm.email, interests: [] });
        } catch (err) {
          // Ignorer si déjà inscrit
        }
      }
      
      toast.success(t('contactExt.successMsg'), {
        description: subscribeNewsletter ? t('contactExt.successDescNewsletter') : t('contactExt.successDesc')
      });
      setContactForm({ name: '', email: '', subject: '', message: '' });
      setSubscribeNewsletter(false);
    } catch (error) {
      toast.error(t('contactExt.errorMsg'), {
        description: t('contactExt.errorDesc')
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen pt-24" data-testid="contact-page">
      {/* Header */}
      <section className="py-16 bg-gradient-to-br from-fuchsia-500/10 via-transparent to-lime-500/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center">
            <span className="text-sm font-medium uppercase tracking-widest text-fuchsia-500 mb-4 block">
              {t('contactExt.contactUs')}
            </span>
            <h1 className="font-['Playfair_Display'] text-4xl md:text-5xl font-bold mb-6">
              {t('contactExt.title')}
            </h1>
            <p className="text-muted-foreground max-w-2xl mx-auto text-lg">
              {t('contactExt.subtitle')}
            </p>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Contact Info */}
            <div className="space-y-6">
              <Card data-testid="contact-info-card">
                <CardContent className="p-6">
                  <h3 className="font-['Playfair_Display'] text-xl font-bold mb-6">
                    {t('contactExt.contactInfo')}
                  </h3>
                  <div className="space-y-4">
                    <a
                      href="tel:+33749513090"
                      className="flex items-start gap-4 group"
                    >
                      <div className="w-10 h-10 rounded-full bg-lime-500/10 flex items-center justify-center flex-shrink-0">
                        <Phone className="h-5 w-5 text-lime-500" />
                      </div>
                      <div>
                        <p className="font-medium group-hover:text-fuchsia-500 transition-colors">
                          {t('contactExt.phone')}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          07 49 51 30 90
                        </p>
                      </div>
                    </a>
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 rounded-full bg-fuchsia-500/10 flex items-center justify-center flex-shrink-0">
                        <MapPin className="h-5 w-5 text-fuchsia-500" />
                      </div>
                      <div>
                        <p className="font-medium">{t('contactExt.address')}</p>
                        <p className="text-sm text-muted-foreground">
                          Domaine de la Briouse<br />
                          La Briouse<br />
                          86600 Sanxay
                        </p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Newsletter */}
              {/* HelloAsso */}
              <Card className="bg-gradient-to-br from-fuchsia-500 to-purple-600 text-white">
                <CardContent className="p-6 text-center">
                  <h3 className="font-['Playfair_Display'] text-xl font-bold mb-3">
                    {t('contactExt.onlineMembership')}
                  </h3>
                  <p className="text-white/90 text-sm mb-4">
                    {t('contactExt.joinViaHelloAsso')}
                  </p>
                  <Link to="/adhesion">
                    <Button
                      className="bg-white text-fuchsia-600 hover:bg-white/90 w-full"
                      data-testid="adhesion-btn"
                    >
                      {t('contactExt.memberFor10')}
                      <ExternalLink className="ml-2 h-4 w-4" />
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              <Card data-testid="contact-form-card">
                <CardContent className="p-8">
                  <h3 className="font-['Playfair_Display'] text-2xl font-bold mb-6">
                    {t('contactExt.sendMessage')}
                  </h3>
                  <form onSubmit={handleContactSubmit} className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label htmlFor="name">{t('contactExt.fullName')}</Label>
                        <Input
                          id="name"
                          type="text"
                          placeholder="Jean Dupont"
                          value={contactForm.name}
                          onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                          required
                          data-testid="contact-name-input"
                          className="form-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="email">{t('contactExt.email')}</Label>
                        <Input
                          id="email"
                          type="email"
                          placeholder="jean@exemple.com"
                          value={contactForm.email}
                          onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                          required
                          data-testid="contact-email-input"
                          className="form-input"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="subject">{t('contactExt.subject')}</Label>
                      <Input
                        id="subject"
                        type="text"
                        placeholder={t('contactExt.subjectPlaceholder')}
                        value={contactForm.subject}
                        onChange={(e) => setContactForm({ ...contactForm, subject: e.target.value })}
                        required
                        data-testid="contact-subject-input"
                        className="form-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="message">{t('contactExt.message')}</Label>
                      <Textarea
                        id="message"
                        placeholder={t('contactExt.messagePlaceholder')}
                        rows={6}
                        value={contactForm.message}
                        onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                        required
                        data-testid="contact-message-input"
                        className="form-input resize-none"
                      />
                    </div>
                    {/* Newsletter Subscription */}
                    <div className="flex items-center gap-3 p-4 bg-lime-50 rounded-lg border border-lime-200">
                      <input
                        type="checkbox"
                        id="newsletter"
                        checked={subscribeNewsletter}
                        onChange={(e) => setSubscribeNewsletter(e.target.checked)}
                        className="h-5 w-5 rounded border-lime-300 text-lime-600 focus:ring-lime-500"
                        data-testid="newsletter-checkbox"
                      />
                      <Label htmlFor="newsletter" className="text-sm text-zinc-700 cursor-pointer">
                        <Mail className="h-4 w-4 inline mr-2 text-lime-600" />
                        {t('contactExt.newsletterCheckbox')}
                      </Label>
                    </div>
                    <Button
                      type="submit"
                      className="btn-primary"
                      disabled={loading}
                      data-testid="contact-submit-btn"
                    >
                      {loading ? (
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      ) : (
                        <Send className="h-4 w-4 mr-2" />
                      )}
                      {t('contactExt.sendBtn')}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Map */}
      <section className="py-16 bg-muted/30">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-12">
            <h2 className="font-['Playfair_Display'] text-3xl font-bold mb-4">
              {t('contactExt.findUs')}
            </h2>
            <p className="text-muted-foreground">
              Domaine de la Briouse, La Briouse, 86600 Sanxay
            </p>
          </div>
          <div className="map-container aspect-video max-w-4xl mx-auto">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2762.5!2d-0.0395!3d46.4875!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x48047487e5555555%3A0x1234567890abcdef!2sLa%20Briouse%2C%2086600%20Sanxay!5e0!3m2!1sfr!2sfr!4v1700000000000!5m2!1sfr!2sfr"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Domaine de la Briouse - La Briouse 86600 Sanxay"
            />
          </div>
        </div>
      </section>
    </div>
  );
}
